"use client";

import { useCallback, useMemo, useState } from "react";
import { Client } from "@/lib/types/database";
import type { User } from "@/lib/types/user";
import { useContacts, useClientSearch, useContactByEmail } from "@/features/contacts/hooks";
import { useUserSearch } from "@/lib/hooks/api/users";
import { useDebounce } from "@/lib/hooks/ui/use-debounce";

interface UseComposeClientStateProps {
  initialClient?: Client;
  toEmail?: string;
}

/**
 * Hook for managing compose client/contact selection state
 * Handles client search, filtering, and selection
 */
export function useComposeClientState({
  initialClient,
  toEmail,
}: UseComposeClientStateProps = {}) {
  const [selectedClient, setSelectedClient] = useState<Client | null>(initialClient || null);
  const [clientSearchTerm, setClientSearchTerm] = useState("");

  const debouncedClientSearch = useDebounce(clientSearchTerm, 300);

  // Data fetching
  const { data: initialClients = [], isLoading: initialClientsLoading } = useContacts({
    limit: 20,
  });

  const { data: searchResults = [], isFetching: isSearching } =
    useClientSearch(debouncedClientSearch);

  const { data: hubUserSearchResults = [], isFetching: hubUsersLoading } =
    useUserSearch(debouncedClientSearch);

  // Contact lookup for existing contacts
  const { data: existingContact } = useContactByEmail(toEmail || "");

  const clientOptions = useMemo<Client[]>(() => {
    const base = debouncedClientSearch ? searchResults : initialClients;
    if (!initialClient) return base;
    const hasInitial = base.some((c: Client) => c.id === initialClient.id);
    if (hasInitial) return base;
    return [initialClient, ...base];
  }, [debouncedClientSearch, searchResults, initialClients, initialClient]);

  const filteredHubUsers = useMemo(() => hubUserSearchResults, [hubUserSearchResults]);

  const handleClientSelect = useCallback((clientId: string) => {
    const client = clientOptions.find((c) => c.id === clientId);
    if (client) {
      setSelectedClient(client);
    }
  }, [clientOptions]);

  const handleClientSearchChange = useCallback((term: string) => {
    setClientSearchTerm(term);
  }, []);

  return useMemo(
    () => ({
      selectedClient,
      clientOptions,
      filteredHubUsers,
      hubUsersLoading,
      clientsLoading: initialClientsLoading,
      isSearching,
      clientSearchTerm,
      existingContact,
      setSelectedClient,
      setClientSearchTerm,
      handleClientSelect,
      handleClientSearchChange,
    }),
    [
      selectedClient,
      clientOptions,
      filteredHubUsers,
      hubUsersLoading,
      initialClientsLoading,
      isSearching,
      clientSearchTerm,
      existingContact,
      handleClientSelect,
      handleClientSearchChange,
    ]
  );
}
